import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import 'mantine-react-table/styles.css';

import {
    ActionsColumn,
    DateTimeColumn,
    DomainColumn,
    IdColumn,
    RegistrarColumn,
    StatusColumn,
} from './components';
import {
    Box,
    Container,
    Flex,
    Group,
    MultiSelect,
    Portal,
    Select,
    Text,
} from '@mantine/core';
import {
    getMessageGroups,
    getShortlinkEvents,
    getShortlinks,
} from '../../../../http/apiClient';
import {
    useDebouncedCallback,
    useDisclosure,
    useLocalStorage,
} from '@mantine/hooks';
import { useMemo, useState } from 'react';

import CampaignGroupStatsTimelineModal from '../../../modals/timeline/CampaignGroupStatsTimelineModal';
import FiltersMultiSelect from '../../../common/filtersMultiSelect/FiltersMultiSelect';
import { MantineReactTable } from 'mantine-react-table';
import ReplaceShortLinkModal from './modals/ReplaceShortLinkModal';
import ResourceEventsModal from '../../../modals/events/EventsModal';
import ShortlinksTopToolbar from './toolbars/ShortlinksTopToolbar';
import { StatColumn } from '../../../common';
import { naiveRound } from '../../../../utils/text';
import { sumObjectKeys } from '../../../../utils/statUtils';
import { usePaginatedQuery } from '../../../../http/queries/usePaginatedQuery';

const ShortlinksView = ({ ...props }) => {
    const [globalFilter, setGlobalFilter] = useState(null);
    const [sorting, setSorting] = useState([]);
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 50,
    });

    const filterDebounce = useDebouncedCallback((query) => {
        setGlobalFilter(query);
    }, 600);

    const [
        detailsModalOpen,
        { open: openDetailsModal, close: closeDetailsModal },
    ] = useDisclosure();

    const [
        replaceModalOpen,
        { open: openReplaceModal, close: closeReplaceModal },
    ] = useDisclosure();

    const [modalTab, setModalTab] = useState('events');
    const [modalItem, setModalItem] = useState(null);

    const [
        timelineModalOpen,
        { open: openTimelineModal, close: closeTimelineModal },
    ] = useDisclosure();

    const {
        isLoading,
        isError,
        error,
        data: paginatedData,
    } = usePaginatedQuery({
        queryFn: (queryParams) => getShortlinks(queryParams),
        queryKey: ['short_links'],
        refetchInterval: 15_0000,
        globalFilter,
        pagination,
        sorting,
    });

    const { data: shortlinks, pagination: dataPagination } =
        paginatedData || {};
    const columns = useMemo(
        () => [
            IdColumn(),
            DomainColumn(),
            RegistrarColumn(),
            StatusColumn(),
            StatColumn('Sent', (x) => x.stats?.send),
            StatColumn('Clicks', (x) =>
                sumObjectKeys(x.stats, ['uniqueClick'])
            ),
            StatColumn('Previews', (x) =>
                sumObjectKeys(x.stats, ['uniquePreview'])
            ),
            StatColumn(
                'CTR',
                (x) => {
                    const uniqueClicks = sumObjectKeys(x.stats, [
                        'uniqueClick',
                    ]);
                    return naiveRound((uniqueClicks / x.stats?.send) * 100, 2);
                },
                true
            ),
            StatColumn('Conversions', (x) => x.stats?.conversion),
            StatColumn(
                'CR',
                (x) => {
                    const uniqueClicks = sumObjectKeys(x.stats, [
                        'uniqueClick',
                    ]);
                    return naiveRound(
                        (x.stats?.conversion / uniqueClicks) * 100,
                        2
                    );
                },
                true
            ),
            DateTimeColumn({
                id: 'purchasedAt',
                header: 'Purchased At',
                accessorFn: (rowData) => rowData.purchasedData,
            }),
            DateTimeColumn({
                id: 'expiresAt',
                header: 'Expires',
                accessorFn: (rowData) => rowData.expiresAt,
            }),
        ],
        [shortlinks]
    );

    const tableData = useMemo(
        () => (isLoading ? [] : shortlinks),
        [shortlinks, isLoading]
    );

    const [columnVisibility, setColumnVisibility] = useLocalStorage({
        key: 'shortlinks-table-visibility', // a unique key for local storage
        defaultValue: {}, // default to all columns shown if empty
        getInitialValueInEffect: true,
    });

    const [columnSizing, setColumnSizing] = useLocalStorage({
        key: 'shortlinks-group-table-sizing', // a unique key for local storage
        defaultValue: {}, // default to all columns shown if empty
        getInitialValueInEffect: true,
    });

    const handleVisibilityChange = (changeFunc) => {
        const changes = changeFunc();
        setColumnVisibility((prevState) => ({
            ...prevState,
            ...changes,
        }));
    };

    const handlSizeChange = (changeFunc) => {
        const changes = changeFunc();
        setColumnSizing((prevState) => ({
            ...prevState,
            ...changes,
        }));
    };

    const handleFilterChange = (queryStr) => {
        filterDebounce(queryStr);
    };

    const tableProps = {
        columns: columns,
        data: tableData,
        enableRowActions: true,
        enableColumnResizing: true,
        enableColumnActions: false,
        enableSorting: false,
        enableTopToolbar: true,
        enableGlobalFilter: true,
        enableFilters: false,
        enableStickyHeader: true,
        enableDensityToggle: false,
        positionActionsColumn: 'last',
        layoutMode: 'semantic',
        mantineSearchTextInputProps: {
            placeholder: 'Search Short Links',
            miw: 200,
        },
        mantineTableContainerProps: {
            style: {
                minHeight: '80vh',
            },
        },
        positionGlobalFilter: 'left',
        renderTopToolbarCustomActions: ({ props: table }) => (
            <ShortlinksTopToolbar table={table} />
        ),
        renderRowActions: (props) => (
            <ActionsColumn
                {...props}
                setTab={setModalTab}
                setModalItem={setModalItem}
                openDetailsModal={openDetailsModal}
                openTimelineModal={openTimelineModal}
                openReplaceModal={openReplaceModal}
            />
        ),

        manualPagination: true,
        manualSorting: true,
        onColumnVisibilityChange: handleVisibilityChange,
        onColumnSizingChange: handlSizeChange,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        onGlobalFilterChange: handleFilterChange,
        rowCount: dataPagination?.totalItems || 0,
        pageCount: dataPagination?.totalPages || 1,

        initialState: {
            columnVisibility,
            pagination,
            isLoading,
            showGlobalFilter: true,
        },
        state: {
            isLoading,
            pagination,
            columnVisibility,
            columnSizing,
            showAlertBanner: isError,
            showProgressBars: isLoading,
            sorting,
            density: 'xs',
        },
    };

    return (
        <Container fluid>
            <MantineReactTable {...tableProps} />
            <ResourceEventsModal
                title={'Domain Events'}
                httpQueryFn={(queryParams) =>
                    getShortlinkEvents({
                        id: modalItem?.id,
                        pagination: queryParams,
                    })
                }
                queryKey={['short_link_events', modalItem?.id]}
                openTab={modalTab}
                opened={detailsModalOpen}
                close={closeDetailsModal}
            />
            <CampaignGroupStatsTimelineModal
                resource={'shortlinks'}
                resourceId={modalItem?.id}
                opened={timelineModalOpen}
                close={closeTimelineModal}
            />
            <ReplaceShortLinkModal
                shortLink={modalItem}
                opened={replaceModalOpen}
                close={closeReplaceModal}
            />
        </Container>
    );
};
export default ShortlinksView;
